import { useState } from 'react'

interface CompetitorLogoProps {
  name: string
  logoUrl?: string | null
  size?: number
  shape?: 'rounded' | 'circle' | 'square'
}

const LOGO_PALETTE: Array<{ bg: string; fg: string; border?: string }> = [
  { bg: '#1d4e8b', fg: '#ffffff' }, // navy
  { bg: '#2d5a3d', fg: '#ffffff' }, // forest
  { bg: '#b43a4a', fg: '#ffffff' }, // raspberry
  { bg: '#d97706', fg: '#ffffff' }, // amber
  { bg: '#e67363', fg: '#ffffff' }, // coral
  { bg: '#7a2e3b', fg: '#ffffff' }, // burgundy
  { bg: '#1a1a1a', fg: '#f0efe9' }, // charcoal
  { bg: '#e8513a', fg: '#ffffff' }, // tomato
  { bg: '#0d1a24', fg: '#ffffff', border: '#283646' }, // ink
  { bg: '#687887', fg: '#ffffff' }, // steel
  { bg: '#f0e6d2', fg: '#4b3a20' }, // cream
  { bg: '#00727a', fg: '#ffffff' }, // teal
  { bg: '#a35f3a', fg: '#ffffff' }, // clay
]

function hashString(s: string) {
  let hash = 5381
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash) + s.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

function monogramFor(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  const word = parts[0]
  if (word.length >= 2) {
    return word[0].toUpperCase() + word[1].toLowerCase()
  }
  return word[0].toUpperCase()
}

export function CompetitorLogo({ name, logoUrl, size = 40, shape = 'rounded' }: CompetitorLogoProps) {
  const [imgFailed, setImgFailed] = useState(false)
  const showImage = Boolean(logoUrl) && !imgFailed

  const radius = shape === 'circle' ? '999px' : shape === 'square' ? '3px' : `${Math.max(4, size * 0.18)}px`
  const palette = LOGO_PALETTE[hashString(name) % LOGO_PALETTE.length]
  const fontSize = Math.round(size * 0.38)

  if (showImage) {
    return (
      <img
        src={logoUrl ?? undefined}
        alt=""
        aria-hidden="true"
        onError={() => setImgFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          objectFit: 'cover',
          background: '#fff',
          border: '1px solid var(--color-border-subtle)',
          flexShrink: 0,
          display: 'block',
        }}
      />
    )
  }

  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: palette.bg,
        color: palette.fg,
        border: palette.border ? `1px solid ${palette.border}` : 'none',
        display: 'inline-grid',
        placeItems: 'center',
        flexShrink: 0,
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize,
        letterSpacing: '-0.02em',
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {monogramFor(name)}
    </span>
  )
}
